import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

const TermsConditionsList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [termsConditions, setTermsConditions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} terms & conditions`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    searchText: ''
  });

  const getTermsConditions = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.searchText,
        ...params
      };
      
      console.log('Fetching terms & conditions with params:', requestParams);
      const response = await masterService.getTermsConditions(requestParams);
      console.log('Terms & Conditions API Response:', response);
      
      if (response.data && response.data.status) {
        // Handle different possible response structures
        const termsConditionsData = response.data.data?.data || response.data.data || [];
        const paginationData = response.data.data?.pagination || response.data.pagination || {};
        
        console.log('Terms & Conditions data:', termsConditionsData);
        console.log('Pagination data:', paginationData);
        
        // Ensure termsConditionsData is an array
        if (Array.isArray(termsConditionsData)) {
          setTermsConditions(termsConditionsData);
        } else {
          console.error('Terms & Conditions data is not an array:', termsConditionsData);
          setTermsConditions([]);
        }
        
        setPagination(prev => ({
          ...prev,
          ...paginationData,
          current: paginationData.currentPage || 1
        }));
      } else {
        message.error('Failed to fetch terms & conditions');
        setTermsConditions([]);
      }
    } catch (error) {
      console.error('Error fetching terms & conditions:', error);
      message.error('Failed to fetch terms & conditions');
      setTermsConditions([]);
    } finally {
      setLoading(false);
    }
  }, [filters.searchText]);

  // Load terms & conditions data
  useEffect(() => {
    getTermsConditions();
  }, [getTermsConditions]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getTermsConditions({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getTermsConditions({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current
    });
  };

  // Handle add terms & conditions
  const handleAddTermsConditions = () => {
    navigate('/master/add_terms_conditions');
  };

  // Handle edit terms & conditions
  const handleEditTermsConditions = (tcId) => {
    navigate(`/master/edit_terms_conditions/${tcId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Order',
      dataIndex: 'tc_order',
      key: 'tc_order',
      width: 80,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.tc_order - b.tc_order,
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Heading',
      dataIndex: 'tc_heading',
      key: 'tc_heading',
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditTermsConditions(record.tc_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Terms & Conditions</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Terms & Conditions List"
      desc="Manage terms and conditions"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Terms & Conditions ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTermsConditions}
              size="large"
            >
              Add Terms & Conditions
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search terms & conditions..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={termsConditions}
          rowKey="tc_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{ 
            emptyText: 'No terms & conditions found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default TermsConditionsList;